import { RenderElementProps, useSlate } from 'slate-react';
import clsx from 'clsx';
import { LinkIcon, Unlink } from 'lucide-react';
import { Editor, Element as SlateElement, Range, Transforms } from 'slate';
import { LinkElement } from './custom-types';
import { CSSProperties, useState } from 'react';
import { isUrl } from '@pioz/mlm-common';
import Link from 'next/link';
import { Dialog } from '../../Dialog';
import { CheckboxInput, TextInput } from '../../form-fields';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

export function AddLinkButton() {
  const editor = useSlate();
  const [close, setClose] = useState(false);

  const {
    register,
    formState: { isValid },
    reset,
    getValues,
  } = useForm({
    defaultValues: { url: '', targetBlank: false },
    resolver: zodResolver(z.object({ url: z.string().min(1), targetBlank: z.boolean() })),
  });

  return (
    <Dialog
      openButtonContent={<LinkIcon />}
      openButtonClass={clsx('btn btn-xs', isLinkActive(editor) && 'btn-primary')}
      onClose={() => {
        reset();
        setClose(false);
      }}
      forceClose={close}
    >
      <h3>Enter the URL of the link</h3>
      <TextInput {...register('url')} />
      <CheckboxInput label="Open in new tab" {...register('targetBlank')} />
      <div className="modal-action">
        <button
          disabled={!isValid}
          type="button"
          className="btn btn-primary"
          onClick={() => {
            const data = getValues();
            wrapLink(editor, data.url, data.targetBlank);
            setTimeout(() => setClose(true), 100);
          }}
        >
          Add
        </button>
      </div>
    </Dialog>
  );
}

export function RemoveLinkButton() {
  const editor = useSlate();

  return (
    <button
      type="button"
      className={clsx('btn btn-xs', isLinkActive(editor) && 'btn-primary')}
      onMouseDown={() => {
        if (isLinkActive(editor)) {
          unwrapLink(editor);
        }
      }}
    >
      <Unlink />
    </button>
  );
}

export const wrapLink = (editor: Editor, url: string, targetBlank: boolean) => {
  if (isLinkActive(editor)) {
    unwrapLink(editor);
  }

  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);
  const link: LinkElement = {
    type: 'link',
    url,
    children: isCollapsed ? [{ text: url }] : [],
    targetBlank,
  };

  if (isCollapsed) {
    Transforms.insertNodes(editor, link);
  } else {
    Transforms.wrapNodes(editor, link, { split: true });
    Transforms.collapse(editor, { edge: 'end' });
  }
};

export const unwrapLink = (editor: Editor) => {
  Transforms.unwrapNodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link',
  });
};

const isLinkActive = (editor: Editor) => {
  const [link] = Editor.nodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link',
  });
  return !!link;
};

interface LinkComponentProps extends RenderElementProps {
  element: LinkElement;
  style: CSSProperties;
}

export function LinkComponent({ attributes, children, element, style }: LinkComponentProps) {
  return (
    <Link
      {...attributes}
      href={element.url}
      className="link link-primary"
      style={style}
      target={element.targetBlank ? '_blank' : undefined}
      scroll={false}
    >
      {children}
    </Link>
  );
}

function InlineChromiumBugfix() {
  return (
    <span contentEditable={false} className="text-[0]">
      {String.fromCodePoint(160) /* Non-breaking space */}
    </span>
  );
}

export const withInlines = (editor: Editor) => {
  const { insertData, insertText, isInline, isElementReadOnly, isSelectable } = editor;

  editor.isInline = (element) =>
    ['link', 'button', 'badge'].includes(element.type) || isInline(element);

  editor.isElementReadOnly = (element) => element.type === 'badge' || isElementReadOnly(element);

  editor.isSelectable = (element) => element.type !== 'badge' && isSelectable(element);

  editor.insertText = (text) => {
    if (text && isUrl(text)) {
      wrapLink(editor, text, true);
    } else {
      insertText(text);
    }
  };

  editor.insertData = (data) => {
    const text = data.getData('text/plain');

    if (text && isUrl(text)) {
      wrapLink(editor, text, true);
    } else {
      insertData(data);
    }
  };

  return editor;
};
